import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Plus, Search, Filter, Pencil, Trash2, X, LayoutDashboard, 
  Puzzle, Settings, User, Check, LayoutGrid, Table2, Mail,
  Calendar, Shield, ChevronDown, ChevronUp, Users as UsersIcon,
  UserCheck, UserX, ShieldCheck, Download, CheckCircle, XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { utils, writeFile } from 'xlsx';

interface User {
  _id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface UsersResponse {
  success: boolean;
  data: User[];
}

interface FilterState {
  name: string;
  email: string;
  role: string;
  status: string;
}

interface StatusMessage {
  type: 'success' | 'error';
  text: string;
}

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

const ROLE_COLORS = {
  'admin': 'bg-purple-100 text-purple-800',
  'user': 'bg-blue-100 text-blue-800',
  'viewer': 'bg-green-100 text-green-800'
};

const STATUS_COLORS = {
  'active': 'bg-emerald-100 text-emerald-800',
  'inactive': 'bg-red-100 text-red-800'
};

const ROLE_LABELS = {
  'admin': 'Administrador',
  'user': 'Usuario',
  'viewer': 'Visualizador'
};

export default function Users() {
  const navigate = useNavigate();
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    name: '',
    email: '',
    role: '',
    status: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserName, setDeleteUserName] = useState('');
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });
  
  const queryClient = useQueryClient();

  // Fetch users
  const { data, isLoading, isError } = useQuery<UsersResponse>({
    queryKey: ['users', filters],
    queryFn: async () => {
      const token = sessionStorage.getItem('token');
      const queryParams = new URLSearchParams();
      
      // Add filter params if they have values
      if (filters.name) queryParams.append('name', filters.name);
      if (filters.email) queryParams.append('email', filters.email);
      if (filters.role) queryParams.append('role', filters.role);
      if (filters.status) queryParams.append('status', filters.status);

      const response = await fetch(`${API_URL}/users?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Error fetching users');
      }
      return response.json();
    },
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar el usuario');
      }
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Error al eliminar el usuario');
      }
      return result;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowDeleteModal(false);
      setUserToDelete(null);
      setDeleteUserName('');
      setStatusMessage({
        type: 'success',
        text: 'Usuario eliminado correctamente'
      });
      // Scroll to top to show the message
      window.scrollTo(0, 0);
    },
    onError: (error: Error) => {
      setShowDeleteModal(false);
      setUserToDelete(null);
      setDeleteUserName('');
      setStatusMessage({
        type: 'error',
        text: error.message || 'Error al eliminar el usuario'
      });
      // Scroll to top to show the message
      window.scrollTo(0, 0);
    }
  });

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedAndFilteredUsers = useMemo(() => {
    if (!data?.data) return [];
    
    let filteredUsers = [...data.data];

    // Apply filters
    if (filters.name) {
      filteredUsers = filteredUsers.filter(user => 
        user.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }
    if (filters.email) {
      filteredUsers = filteredUsers.filter(user => 
        user.email.toLowerCase().includes(filters.email.toLowerCase())
      );
    }
    if (filters.role) {
      filteredUsers = filteredUsers.filter(user => 
        user.role === filters.role
      );
    }
    if (filters.status) {
      const isActive = filters.status === 'active';
      filteredUsers = filteredUsers.filter(user => 
        user.isActive === isActive
      );
    }

    // Apply sorting
    return filteredUsers.sort((a, b) => {
      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (sortConfig.key === 'email') {
        return sortConfig.direction === 'asc'
          ? a.email.localeCompare(b.email)
          : b.email.localeCompare(a.email);
      }
      return 0;
    });
  }, [data?.data, filters, sortConfig]);

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete && deleteUserName === userToDelete.name) {
      deleteMutation.mutateAsync(userToDelete._id);
    }
  };

  const statsData = useMemo(() => {
    if (!data?.data) return {
      totalUsers: 0,
      activeUsers: 0,
      inactiveUsers: 0,
      adminUsers: 0
    };

    return {
      totalUsers: data.data.length,
      activeUsers: data.data.filter(user => user.isActive).length,
      inactiveUsers: data.data.filter(user => !user.isActive).length,
      adminUsers: data.data.filter(user => user.role === 'admin').length
    };
  }, [data?.data]);

  const handleExportToExcel = () => {
    if (!data?.data) return;

    // Preparar los datos para Excel
    const excelData = data.data.map(user => ({
      'ID': user._id,
      'Nombre': user.name,
      'Email': user.email,
      'Rol': ROLE_LABELS[user.role as keyof typeof ROLE_LABELS],
      'Estado': user.isActive ? 'Activo' : 'Inactivo',
      'Fecha de Creación': format(new Date(user.createdAt), 'dd/MM/yyyy HH:mm', { locale: es }),
      'Última Actualización': format(new Date(user.updatedAt), 'dd/MM/yyyy HH:mm', { locale: es })
    }));

    // Crear el libro de Excel
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Ajustar el ancho de las columnas
    const columnWidths = [
      { wch: 25 },  // ID
      { wch: 30 },  // Nombre
      { wch: 35 },  // Email
      { wch: 15 },  // Rol
      { wch: 12 },  // Estado
      { wch: 20 },  // Fecha de Creación
      { wch: 20 },  // Última Actualización
    ];
    ws['!cols'] = columnWidths;

    // Agregar la hoja al libro
    utils.book_append_sheet(wb, ws, 'Usuarios');

    // Generar el nombre del archivo con la fecha actual
    const fileName = `Usuarios_Export_${format(new Date(), 'dd-MM-yyyy_HH-mm', { locale: es })}.xlsx`;

    // Descargar el archivo
    writeFile(wb, fileName);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Usuarios</h1>
            <p className="mt-1 text-sm text-slate-500">
              Gestiona los usuarios y sus permisos
            </p>
          </div>
          
          {statusMessage && (
            <div className={`w-full sm:w-auto p-4 rounded-lg flex items-center gap-2 ${
              statusMessage.type === 'success' 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {statusMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              <span>{statusMessage.text}</span>
              <button 
                onClick={() => setStatusMessage(null)}
                className="ml-2 hover:opacity-70"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          <div className="flex gap-2">
            <button
              onClick={handleExportToExcel}
              className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
              disabled={isLoading || isError || !data?.data}
            >
              <Download className="w-5 h-5" />
              Exportar a Excel
            </button>
            <button 
              onClick={() => navigate('/users/new')}
              className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nuevo Usuario
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Users */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Usuarios</p>
                <p className="text-2xl font-bold text-slate-900">{statsData.totalUsers}</p>
              </div>
              <div className="p-2 bg-primary-50 rounded-lg">
                <UsersIcon className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </div>

          {/* Active Users */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Usuarios Activos</p>
                <p className="text-2xl font-bold text-emerald-600">{statsData.activeUsers}</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded-lg">
                <UserCheck className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>

          {/* Inactive Users */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Usuarios Inactivos</p>
                <p className="text-2xl font-bold text-red-600">{statsData.inactiveUsers}</p>
              </div>
              <div className="p-2 bg-red-50 rounded-lg">
                <UserX className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          {/* Admin Users */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Administradores</p>
                <p className="text-2xl font-bold text-purple-600">{statsData.adminUsers}</p>
              </div>
              <div className="p-2 bg-purple-50 rounded-lg">
                <ShieldCheck className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200">
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : isError ? (
              <div className="flex items-center justify-center h-64 text-red-600">
                Error al cargar los usuarios
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-y border-slate-200">
                      <th className="py-3 px-4">
                        <div className="space-y-2">
                          <button 
                            className="flex items-center gap-2 text-sm font-semibold text-slate-600"
                            onClick={() => handleSort('name')}
                          >
                            Nombre
                            {sortConfig.key === 'name' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="w-4 h-4" /> : 
                                <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <input
                            type="text"
                            placeholder="Buscar..."
                            value={filters.name}
                            onChange={(e) => setFilters(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                      </th>
                      <th className="py-3 px-4">
                        <div className="space-y-2">
                          <button 
                            className="flex items-center gap-2 text-sm font-semibold text-slate-600"
                            onClick={() => handleSort('email')}
                          >
                            Email
                            {sortConfig.key === 'email' && (
                              sortConfig.direction === 'asc' ? 
                                <ChevronUp className="w-4 h-4" /> : 
                                <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <input
                            type="text"
                            placeholder="Buscar..."
                            value={filters.email}
                            onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          />
                        </div>
                      </th>
                      <th className="py-3 px-4">
                        <div className="space-y-2">
                          <span className="block text-sm font-semibold text-slate-600">Rol</span>
                          <select
                            value={filters.role}
                            onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="">Todos</option>
                            <option value="admin">Administrador</option>
                            <option value="user">Usuario</option>
                            <option value="viewer">Visualizador</option>
                          </select>
                        </div>
                      </th>
                      <th className="py-3 px-4">
                        <div className="space-y-2">
                          <span className="block text-sm font-semibold text-slate-600">Estado</span>
                          <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="">Todos</option>
                            <option value="active">Activo</option>
                            <option value="inactive">Inactivo</option>
                          </select>
                        </div>
                      </th>
                      <th className="py-3 px-4">
                        <span className="block text-sm font-semibold text-slate-600">Acciones</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAndFilteredUsers.map((user) => (
                      <tr key={user._id} className="border-b border-slate-200 hover:bg-slate-50">
                        <td className="py-4 px-4">
                          <div className="font-medium text-slate-900">{user.name}</div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="text-sm text-slate-600">{user.email}</div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`
                            inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${ROLE_COLORS[user.role as keyof typeof ROLE_COLORS]}
                          `}>
                            {ROLE_LABELS[user.role as keyof typeof ROLE_LABELS]}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`
                            inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${user.isActive ? STATUS_COLORS.active : STATUS_COLORS.inactive}
                          `}>
                            {user.isActive ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/users/${user._id}/edit`)}
                              className="p-1 text-slate-600 hover:text-primary-600 transition-colors"
                              title="Editar"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(user)}
                              className="p-1 text-slate-600 hover:text-red-600 transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Results Count */}
          {!isLoading && !isError && sortedAndFilteredUsers && (
            <div className="px-4 py-3 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">
                  Mostrando {sortedAndFilteredUsers.length} resultado{sortedAndFilteredUsers.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Confirmar eliminación
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Esta acción no se puede deshacer. Para confirmar, escribe el nombre del usuario: 
                <span className="font-semibold">{userToDelete?.name}</span>
              </p>
              <input
                type="text"
                value={deleteUserName}
                onChange={(e) => setDeleteUserName(e.target.value)}
                placeholder="Nombre del usuario"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                    setDeleteUserName('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleteUserName !== userToDelete?.name}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Eliminar Usuario
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
} 